/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useIntl } from 'react-intl';
import TextButton from '../common/button/TextButton';
import Inputfield from '../common/forms/Inputfield';
import Translate from '../locale/Translate';
import Button from '../common/button/Button';
import { useApi } from '../../api/apiProvider';
import { SERVICE_ID } from '../../api/serviceInfo';
import Spinner from '../common/spinner/Spinner';
import { useUserActions } from './UserActionProvider';
import { useYupValidationResolver } from './useYupValidationResolver';

const RegistrerForm = ({
  onRegisterSuccess,
}: {
  onRegisterSuccess: { (user: { username: string; epostSendt: boolean }): void };
}): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [userExists, setUserExists] = useState(false);

  const { formatMessage } = useIntl();
  const { urls, api } = useApi();
  const { toggleGlemtPassord } = useUserActions();

  const registerUserSchema = useMemo(
    () =>
      yup.object({
        username: yup.string().lowercase().required('validatation.required').email('validatation.notavalidemail'),
        acceptTermsAndConditions: yup.bool().oneOf([true], 'validatation.termsandcondition'),
        hcUser: yup.bool(),
      }),
    []
  );

  const resolver = useYupValidationResolver(registerUserSchema);
  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
  } = useForm<{
    username: string;
    hcUser: boolean;
  }>({
    mode: 'onSubmit',
    resolver,
  });

  const registerUser = handleSubmit(async ({ username }) => {
    setLoading(true);
    setUserExists(false);
    try {
      const url = urls.registerUser();
      const res = await api.post(url, {
        navn: username,
        epost: username,
        serviceId: SERVICE_ID,
      });
      const epostSendt = res.data.result.epostSendt;

      if (onRegisterSuccess) onRegisterSuccess({ username, epostSendt });
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      if (e?.response?.data.errorMessage.includes('ErrorId: 851a41f7-1b8a-41fa-b9f4-716cde59e77a')) {
        setUserExists(true);
        setError('username', { type: 'conflict', message: 'validatation.emailconflict.short' });
      }
    }
  });

  return (
    <>
      <form name="create_user_form" onSubmit={registerUser}>
        <Inputfield
          autoFocus
          autoComplete="off"
          type="email"
          label={formatMessage({
            id: 'user.form-email',
            defaultMessage: 'Din E-post',
          })}
          error={
            errors.username?.message &&
            formatMessage({
              id: errors.username.message.toString(),
              defaultMessage: ' ',
            })
          }
          {...register('username')}
          required
        />

        <Button
          type="submit"
          form="create_user_form"
          className="w-full text-5"
          disabled={loading}
          onClick={registerUser}
        >
          <Translate id="registration.form-submit" />
        </Button>
      </form>
      {userExists && (
        <div className="p-3 mt-2 whitespace-pre-line bg-state-warning">
          <Translate
            id="registration.form-forgotPasword"
            values={{
              glemtPasordLink: (chunks: ReactNode) => (
                <TextButton
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleGlemtPassord();
                  }}
                >
                  {chunks}
                </TextButton>
              ),
            }}
          />
        </div>
      )}
      {loading && <Spinner />}
    </>
  );
};

export default RegistrerForm;
